// src/oura/components/site/pageSpeed.ts
//
// What the Core Web Vitals card knows about a PageSpeed Insights run: which
// value counts as measured, how each one is rated, what it means in plain
// words, and how it moved since a Re-scan. Moved out of SiteAudit.tsx
// unchanged in behaviour; the card and the Site tiles both read it.

import type { PageSpeedRun, PsOpportunity } from "../../audit";

export type Rating = "good" | "needs-improvement" | "poor";

export const RATING_TEXT: Record<Rating, string> = {
    good: "text-emerald-700",
    "needs-improvement": "text-amber-700",
    poor: "text-rose-700",
};

export const secs = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
export const rate = (v: number, good: number, poor: number): Rating =>
    v <= good ? "good" : v <= poor ? "needs-improvement" : "poor";

/**
 * Google's own PageSpeed bands for a 0 to 100 Lighthouse score: 90 and up is
 * good, 50 and up needs work. The four gauges are PageSpeed's picture, so they
 * keep PageSpeed's colours rather than the Site Health bands.
 */
export function psiBand(v: number): { ring: string; text: string } {
    if (v >= 90) return { ring: "#10B981", text: "text-emerald-700" };
    if (v >= 50) return { ring: "#F59E0B", text: "text-amber-700" };
    return { ring: "#E11D48", text: "text-rose-700" };
}

/**
 * "PSI did not return this", as distinct from "PSI measured this and it is zero".
 *
 * This is the defect the card was reported for. On a site CrUX held no INP
 * samples for, `inpMs: 0` rendered as a green "0 ms" tagged CORE: a perfect
 * score for something that was never measured.
 *
 * `null` is now the honest signal on the wire (ourteam-backend#4470), so the
 * question is simply whether a value arrived. This used to ALSO read 0 as
 * absent for LCP/FCP/SI/INP, a stopgap for the era when the BE sent 0 for
 * anything it could not read, deliberately never applied to CLS or TBT, where
 * 0 is a genuine and common good result. With null on the wire that stopgap
 * only hides real measurements, so it is gone.
 */
export const notMeasured = (v: number | null | undefined): v is null | undefined => v === null || v === undefined;

/**
 * What each metric actually tells you, in plain language.
 *
 * Every tile carries one. "LCP 23.6 s" means nothing to the person paying for
 * the site, and the acronym grid was the part of this card that read as data
 * dumped rather than a finding explained. No jargon, no acronyms expanded into
 * more jargon: say what the number is measuring in one sentence.
 */
export const METRIC_HELP: Record<string, string> = {
    "Core Web Vitals Assessment": "Google's overall pass or fail for this page, taken from real visitors.",
    "Interaction to Next Paint": "How quickly the page reacts after someone taps or clicks.",
    "Largest Contentful Paint": "Time until the biggest thing on screen finishes loading.",
    "Cumulative Layout Shift": "How much the page jumps around as it loads.",
    "First Contentful Paint": "Time until anything at all appears on screen.",
    "Total Blocking Time": "How long the page sat frozen while loading.",
    "Speed Index": "How quickly the page visibly fills in.",
    "Time to First Byte": "How long the server took to respond.",
};

/**
 * Lighthouse's opportunities, translated for the person who actually reads
 * this card.
 *
 * "Reduce unused JavaScript" is Lighthouse talking to a developer: it names a
 * symptom in a vocabulary the reader does not have. Oura's reader is a clinic
 * owner or a marketer, so the one section meant to be actionable was the least
 * actionable thing here. Each line says what the finding MEANS and, just as
 * importantly, WHO usually fixes it, because "I can do this in my CMS" and
 * "this is a job for my developer or host" are completely different next steps
 * and the label alone never distinguishes them.
 *
 * **An unknown opportunity falls back to no explanation at all.** A wrong
 * plain-English gloss on a finding we did not recognise is worse than the bare
 * label, because the reader would act on it. Google adds and renames audits,
 * so misses are expected and must fail silent.
 *
 * Keyed by the Lighthouse audit `id`, which `_opportunities()` sends since
 * ourteam-backend#4491 and which Lighthouse keeps stable. The titles stay as a
 * fallback for runs stored before the id was sent, and list every wording
 * Google has used (next-gen became modern formats), because matching on text
 * is exactly the fragility the id removes.
 */
const OPPORTUNITIES: Array<{ id: string; titles: string[]; help: string }> = [
    { id: "unused-javascript", titles: ["reduce unused javascript"], help: "Unused plugin or tracking code. A developer can remove it." },
    { id: "unused-css-rules", titles: ["reduce unused css"], help: "Leftover theme or plugin styling. A developer can strip it out." },
    { id: "render-blocking-resources", titles: ["eliminate render-blocking resources"], help: "Files that delay the first paint. A developer can load them later." },
    { id: "uses-responsive-images", titles: ["properly size images"], help: "Images larger than the space they fill. Re-upload them smaller." },
    {
        id: "modern-image-formats",
        titles: ["serve images in next-gen formats", "serve images in modern formats"],
        help: "An older image format. Newer ones look identical at half the size.",
    },
    { id: "uses-optimized-images", titles: ["efficiently encode images"], help: "Images heavier than they need to be. Compress them before uploading." },
    { id: "offscreen-images", titles: ["defer offscreen images"], help: "Images below the fold load too early. A developer can delay them." },
    { id: "uses-text-compression", titles: ["enable text compression"], help: "Text is sent uncompressed. Your host can switch compression on." },
    { id: "server-response-time", titles: ["reduce initial server response time"], help: "The server is slow to answer. Usually a hosting or caching issue." },
    { id: "redirects", titles: ["avoid multiple page redirects"], help: "Visitors pass through extra addresses. Link straight to the final one." },
    { id: "uses-rel-preconnect", titles: ["preconnect to required origins"], help: "The browser reaches other services late. A developer can fix it." },
    { id: "uses-rel-preload", titles: ["preload key requests"], help: "Important files are found late. A developer can fetch them sooner." },
    { id: "unminified-css", titles: ["minify css"], help: "Style files carry spacing browsers ignore. A build step removes it." },
    { id: "unminified-javascript", titles: ["minify javascript"], help: "Code files carry spacing browsers ignore. A build step removes it." },
    { id: "total-byte-weight", titles: ["avoid enormous network payloads"], help: "The page is very heavy, usually large images or too many scripts." },
    { id: "font-display", titles: ["ensure text remains visible during webfont load"], help: "Text is invisible while fonts load. A developer can show a fallback." },
    { id: "legacy-javascript", titles: ["avoid serving legacy javascript to modern browsers"], help: "Extra code only old browsers need. A developer can drop it." },
    { id: "duplicated-javascript", titles: ["remove duplicate modules in javascript bundles"], help: "The same code is included twice. A developer can de-duplicate it." },
    { id: "efficient-animated-content", titles: ["use video formats for animated content"], help: "Animated GIFs are far heavier than video. Converting cuts the size." },
    { id: "prioritize-lcp-image", titles: ["preload largest contentful paint image"], help: "The main image is found late. A developer can load it first." },
    { id: "bootup-time", titles: ["reduce javascript execution time"], help: "The browser runs code for too long, usually plugins or trackers." },
    { id: "mainthread-work-breakdown", titles: ["minimize main-thread work"], help: "The browser is busy processing instead of showing the page." },
    { id: "critical-request-chains", titles: ["avoid chaining critical requests"], help: "Files load one after another. A developer can flatten the chain." },
    { id: "layout-shifts", titles: ["avoid large layout shifts"], help: "Page parts move after appearing, usually images without reserved space." },
];

const HELP_BY_ID = new Map(OPPORTUNITIES.map((o) => [o.id, o.help]));
const HELP_BY_TITLE = new Map(OPPORTUNITIES.flatMap((o) => o.titles.map((t) => [t, o.help] as const)));

/** The id first; then the normalised title, because Lighthouse titles vary in
 *  case and trailing punctuation and a near-miss must return nothing rather
 *  than a neighbouring entry. */
export const opportunityHelp = (o: PsOpportunity): string | undefined =>
    (o.id ? HELP_BY_ID.get(o.id) : undefined)
    ?? HELP_BY_TITLE.get((o.label || "").trim().toLowerCase().replace(/[.:]+$/, ""));

/** A rendered metric. `value === null` means not measured, never a number. */
export type Metric = { label: string; value: string | null; rating: Rating | null; core: boolean };

const metric = (
    label: string,
    v: number | null | undefined,
    fmt: (n: number) => string,
    good: number,
    poor: number,
    opts: { core?: boolean } = {},
): Metric =>
    notMeasured(v)
        ? { label, value: null, rating: null, core: !!opts.core }
        : { label, value: fmt(v), rating: rate(v, good, poor), core: !!opts.core };

/**
 * The LAB metrics: a simulated load on Google's hardware.
 *
 * Google prints "values are estimated and may vary" under these for a reason.
 * On a script-heavy site TBT alone has been measured swinging 112 ms -> 1642 ms
 * across four runs of the SAME url minutes apart, moving the performance score
 * 55 -> 30 by itself, while LCP/FCP/SI stayed within 1%. That is why these live
 * under their own heading with that caveat stated, rather than mixed in with
 * the field numbers as though both were one reading of one thing.
 *
 * In PageSpeed's own order (FCP, LCP, TBT, CLS, SI), so a reader holding the
 * two reports side by side finds each number in the same place.
 *
 * INP is deliberately absent: Lighthouse cannot measure it at all, so it is
 * field data and renders in the field band instead.
 */
export const labMetrics = (r: PageSpeedRun): Metric[] => [
    metric("First Contentful Paint", r.fcpMs, secs, 1800, 3000),
    metric("Largest Contentful Paint", r.lcpMs, secs, 2500, 4000, { core: true }),
    metric("Total Blocking Time", r.tbtMs, (n) => `${n} ms`, 200, 600),
    metric("Cumulative Layout Shift", r.cls, (n) => n.toFixed(2), 0.1, 0.25, { core: true }),
    metric("Speed Index", r.siMs, secs, 3400, 5800),
    // Sixth, and not filler. The BE has always sent `ttfbMs` and this card
    // once discarded it, so a metric we already pay a PSI call for was thrown
    // away. It also completes a 3x2 grid.
    // Google's server-response bar is 800ms good / 1800ms poor.
    metric("Time to First Byte", r.ttfbMs, secs, 800, 1800),
];

/** The one FIELD metric that carries a number: 75th percentile over real
 *  Chrome visitors, same source as the Core Web Vitals Assessment beside it. */
export const fieldInp = (r: PageSpeedRun): Metric =>
    metric("Interaction to Next Paint", r.inpMs, (n) => `${n} ms`, 200, 500, { core: true });

/** A metric's movement since the last Re-scan. `better` carries the DIRECTION,
 *  which is not the sign: for every metric here lower is better, but for the
 *  performance score higher is. Computing it at the source stops each render
 *  site guessing which way is good. Both sides are the same PSI method, so a
 *  change here is a real change. */
export type MetricDelta = { text: string; better: boolean };

export const deltaOf = (
    now: number | null | undefined,
    before: number | null | undefined,
    fmt: (n: number) => string,
    lowerIsBetter = true,
): MetricDelta | null => {
    if (typeof now !== "number" || typeof before !== "number" || now === before) return null;
    const diff = now - before;
    const better = lowerIsBetter ? diff < 0 : diff > 0;
    return { text: `${diff > 0 ? "+" : "-"}${fmt(Math.abs(diff))}`, better };
};

/** Each lab tile back to the field it came from, so a tile can show its own
 *  movement without every call site repeating the field name and formatter. */
export const labDelta = (label: string, now: PageSpeedRun, was?: PageSpeedRun): MetricDelta | null => {
    if (!was) return null;
    switch (label) {
        case "Largest Contentful Paint": return deltaOf(now.lcpMs, was.lcpMs, secs);
        case "Cumulative Layout Shift": return deltaOf(now.cls, was.cls, (n) => n.toFixed(2));
        case "First Contentful Paint": return deltaOf(now.fcpMs, was.fcpMs, secs);
        case "Total Blocking Time": return deltaOf(now.tbtMs, was.tbtMs, (n) => `${n} ms`);
        case "Speed Index": return deltaOf(now.siMs, was.siMs, secs);
        case "Time to First Byte": return deltaOf(now.ttfbMs, was.ttfbMs, secs);
        default: return null;
    }
};

/**
 * The key pages slowest first, by their phone performance score, so the row
 * that most needs work leads. A page PSI could not score goes last: it has no
 * place in a ranking, and leading with it would read as the slowest.
 */
export function slowestFirst<T extends { performance: number | null }>(pages: T[]): T[] {
    const rank = (p: T) => (notMeasured(p.performance) ? Number.POSITIVE_INFINITY : p.performance);
    // `|| 0`: two unmeasured pages give Infinity - Infinity, which is NaN.
    return [...pages].sort((a, b) => rank(a) - rank(b) || 0);
}

/**
 * Which device the card opens on.
 *
 * Desktop first. The reviewer opening Core Web Vitals is looking at the site
 * on a desktop, next to the desktop screenshot and the desktop audit. Landing
 * on mobile meant the first numbers read did not match the page beside them.
 * Mobile is one click away: this changes which one is shown first, not which
 * are measured.
 *
 * But a scan can carry only one strategy, and opening straight onto the empty
 * "No desktop run captured" state for a site measured perfectly well on
 * mobile would trade one wrong first impression for a worse one. Desktop when
 * it exists, the run that does exist otherwise, and desktop when there is
 * nothing at all (the card then shows the never-measured state anyway).
 */
export function defaultStrategy(runs: PageSpeedRun[]): PageSpeedRun["strategy"] {
    return runs.some((x) => x.strategy === "desktop") ? "desktop" : runs[0]?.strategy ?? "desktop";
}
